import React, { useState, useEffect } from 'react';
import SettingsService from '../../../services/SettingsService';
import { PageHeader, Card, Button, Field, Input, Toggle, Badge } from '../../ui';

const REDIRECT_DEFAULT = `${window.location.origin}/login`;

const SocialLoginSettings = () => {
  const [form, setForm] = useState({
    enable_social_login: false,
    google_client_id: '',
    apple_client_id: '',
    apple_redirect_uri: '',
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await SettingsService.getSocialSettings();
        if (res.success) setForm((f) => ({ ...f, ...res.settings }));
      } catch (_) { /* ignore */ } finally { setLoading(false); }
    })();
  }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async () => {
    setSaving(true);
    setMsg(null);
    try {
      const res = await SettingsService.updateSocialSettings({
        ...form,
        apple_redirect_uri: form.apple_redirect_uri || REDIRECT_DEFAULT,
      });
      setMsg({ ok: !!res.success, text: res.success ? 'Saved. Changes are live immediately.' : (res.error || 'Failed to save') });
    } catch (e) {
      setMsg({ ok: false, text: e.error || 'Failed to save' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="tw-py-16 tw-text-center"><i className="fa-solid fa-spinner fa-spin tw-text-2xl tw-text-brand" /></div>;
  }

  return (
    <div className="tw-mx-auto tw-max-w-3xl">
      <PageHeader
        title="Social Login"
        subtitle="Let users sign in with Google and Apple"
        actions={<Button icon="fa-floppy-disk" onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save'}</Button>}
      />

      {msg && (
        <div className={`tw-mb-5 tw-rounded-xl tw-border tw-px-4 tw-py-3 tw-text-sm ${msg.ok ? 'tw-border-good/30 tw-bg-good/10 tw-text-good' : 'tw-border-live/30 tw-bg-live/10 tw-text-live'}`}>
          {msg.text}
        </div>
      )}

      <Card className="tw-mb-6">
        <div className="tw-flex tw-items-center tw-justify-between">
          <div>
            <div className="tw-font-semibold tw-text-heading">Enable social sign-in</div>
            <div className="tw-text-sm tw-text-content-muted">Shows the Google / Apple buttons on the login & signup pages.</div>
          </div>
          <Toggle checked={form.enable_social_login} onChange={(v) => setForm((f) => ({ ...f, enable_social_login: v }))} />
        </div>
      </Card>

      <Card title={<span><i className="fa-brands fa-google tw-mr-2 tw-text-brand" />Google</span>} className="tw-mb-6">
        <Field
          label="OAuth Client ID"
          hint="Google Cloud Console → APIs & Services → Credentials → OAuth 2.0 Client ID (Web application). Add https://app.teve.ca to Authorized JavaScript origins."
        >
          <Input value={form.google_client_id} onChange={set('google_client_id')} placeholder="xxxxxxxx.apps.googleusercontent.com" />
        </Field>
        <div className="tw-mt-2">
          {form.google_client_id ? <Badge tone="good">configured</Badge> : <Badge tone="neutral">not set</Badge>}
        </div>
      </Card>

      <Card title={<span><i className="fa-brands fa-apple tw-mr-2" />Apple</span>}>
        <div className="tw-space-y-4">
          <Field
            label="Services ID (client_id)"
            hint="Apple Developer → Certificates, IDs & Profiles → Identifiers → Services IDs. Enable 'Sign In with Apple' and configure the domain + return URL below."
          >
            <Input value={form.apple_client_id} onChange={set('apple_client_id')} placeholder="ca.teve.web" />
          </Field>
          <Field label="Return URL" hint="Must exactly match a Return URL registered on the Apple Services ID.">
            <Input value={form.apple_redirect_uri} onChange={set('apple_redirect_uri')} placeholder={REDIRECT_DEFAULT} />
          </Field>
        </div>
        <div className="tw-mt-2">
          {form.apple_client_id ? <Badge tone="good">configured</Badge> : <Badge tone="neutral">not set</Badge>}
        </div>
      </Card>

      <p className="tw-mt-6 tw-text-xs tw-text-content-faint">
        No client secrets are stored here — Google and Apple identity tokens are verified against the providers' public keys.
        Full setup steps: <code>SOCIAL_LOGIN_SETUP.md</code> in the repo.
      </p>
    </div>
  );
};

export default SocialLoginSettings;
